import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  Box,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ShoppingCart, FavoriteBorder, Favorite } from "@mui/icons-material";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { useStore } from "../StoreContext/StoreContext";
import { OrderCreateApi, OrderUpdateApi } from "../../Utility/UtilApi/UtilApi";
import { buttonColor, buttonHoverColor, fontFamilyall } from "../../UtilColorPalate/UtilColorPalate";

const ProductPage = () => {
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [wishlist, setWishlist] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    hasExistingOrder,
    OrderRecordId,
    dealLineItems,
    hasExistingWishList,
    wishListRecordId,
    wishListItems,
    fetchCurrentDeals,
    isLoggedIn,
    localCartItems,
    updateLocalCart,
    localWishlist,
    updateLocalWishlist,
    getAndUpdateCartCount,
  } = useStore();

  const product = location.state?.product;
  const initialWishlistState = location.state?.wishlist || false;
  const currencyCode = location.state?.currencyCode;

  useEffect(() => {
    // Initialize wishlist state
    if (!isLoggedIn) {
      const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
      setWishlist(storedWishlist.some((item) => item.id === product?.id));
    } else {
      setWishlist(initialWishlistState);
    }

    // Set initial selected image
    if (product && !selectedImage) {
      const images = [
        product.imageone,
        product.imagetwo,
        product.imagethree,
        product.imagefour,
        product.imagefive,
      ].filter((img) => img && img.trim() !== "");
      setSelectedImage(images[0] || "https://via.placeholder.com/500");
    }

    // Sync quantity from cart if in cart
    if (product) {
      if (!isLoggedIn) {
        const existingItem = localCartItems.find((item) => item.id === product.id);
        if (existingItem) {
          setQuantity(existingItem.quantity);
        }
      } else if (hasExistingOrder && dealLineItems.length > 0) {
        const existingItem = dealLineItems.find(
          (item) => (item.product.id || item.product) === product.id
        );
        if (existingItem) {
          setQuantity(existingItem.quantity);
        }
      }
    }
  }, [
    initialWishlistState,
    product,
    selectedImage,
    isLoggedIn,
    hasExistingOrder,
    dealLineItems,
    localCartItems,
  ]);

  // Sync localWishlist to localStorage when it changes (for unauthenticated users)
  useEffect(() => {
    if (!isLoggedIn && product) {
      localStorage.setItem("wishlist", JSON.stringify(localWishlist));
    }
  }, [localWishlist, isLoggedIn, product]);

  if (!product) {
    return (
      <Container sx={{ textAlign: "center" }}>
        <Typography variant="h5" color="error">
          Product not found. Please return to the shop page.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/shop")}
          sx={{ mt: 2, backgroundColor: "#8c7aa2", "&:hover": { backgroundColor: "#6b5b8e" } }}
        >
          Back to Shop
        </Button>
      </Container>
    );
  }

  const createDeal = async (product, type, qty) => {
    const generateName = () => `${type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const payload = {
      order: {
        name: generateName(),
        type: type,
        stage: "In Progress",
      },
      deallineitems: [
        {
          name: product.name,
          product: product.id,
          quantity: qty,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        },
      ],
    };

    try {
      const response = await OrderCreateApi(payload);
      console.log(`${type} created:`, response.data);
      setToastMessage(response.message || `${type} created and product added!`);
      setToastColor(response.success ? "success" : "warning");
      setSnackbarOpen(true);
      await fetchCurrentDeals();
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Failed to create order");
      setToastColor("error");
      setSnackbarOpen(true);
    }
  };

  const updateDeal = async (product, type, recordId, existingItems, qty, isAdding = true) => {
    if (!recordId) {
      console.error(`${type} record ID is missing`);
      setToastMessage(`Error: No ${type} ID available.`);
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    let updatedLineItems = existingItems.map((item) => ({
      recordid: item.recordid,
      name: item.name,
      product: item.product.id || item.product,
      quantity: item.quantity,
      unitpriceexclvat: item.unitpriceexclvat,
      vat: item.vat,
      discount: item.discount || 0,
    }));

    const existingLineItemIndex = updatedLineItems.findIndex(
      (item) => item.product === product.id
    );

    if (isAdding) {
      if (existingLineItemIndex !== -1) {
        updatedLineItems[existingLineItemIndex].quantity += qty;
      } else {
        updatedLineItems.push({
          name: product.name,
          product: product.id,
          quantity: qty,
          unitpriceexclvat: product.unitpriceexclvat,
          vat: product.vat || 0,
          discount: product.discount || 0,
        });
      }

      const payload = {
        order: {
          recordid: recordId,
          type: type,
          stage: "In Progress",
        },
        deallineitems: updatedLineItems,
      };

      try {
        const response = await OrderUpdateApi(payload);
        setToastMessage(response.message || `Product added to ${type}!`);
        setToastColor(response.success ? "success" : "warning");
        setSnackbarOpen(true);
        await fetchCurrentDeals();
      } catch (error) {
        setToastMessage(error?.response?.data?.message || "Failed to update order");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    } else {
      if (existingLineItemIndex === -1) return;

      const itemToRemove = updatedLineItems[existingLineItemIndex];
      updatedLineItems = updatedLineItems.filter((item) => item.product !== product.id);

      const payload = {
        order: {
          recordid: recordId,
          type: type,
          stage: "In Progress",
        },
        deallineitems: updatedLineItems,
        remove: [itemToRemove.recordid],
      };

      try {
        const response = await OrderUpdateApi(payload);
        setToastMessage(response.message || `Product removed from ${type}!`);
        setToastColor(response.success ? "success" : "warning");
        setSnackbarOpen(true);
        await fetchCurrentDeals();
      } catch (error) {
        setToastMessage(`Failed to remove from ${type}.`);
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleQuantityChange = (event) => {
    const value = Math.max(1, parseInt(event.target.value) || 1);
    setQuantity(value);
  };

  const handleAddToCart = async () => {
    if (!isLoggedIn) {
      const cartItem = {
        id: product.id,
        name: product.name,
        unitpriceexclvat: product.unitpriceexclvat,
        vat: product.vat || 0,
        discount: product.discount || 0,
        quantity: quantity,
      };

      const existingItem = localCartItems.find((item) => item.id === product.id);
      let updatedCart;

      if (existingItem) {
        updatedCart = localCartItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        updatedCart = [...localCartItems, cartItem];
      }

      updateLocalCart(updatedCart);
      setToastMessage("Product added to cart!");
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      try {
        if (!hasExistingOrder) {
          await createDeal(product, "Order", quantity);
        } else {
          await updateDeal(product, "Order", OrderRecordId, dealLineItems, quantity);
        }
        setToastMessage("Product added to cart!");
        setToastColor("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error in handleAddToCart:", error);
        setToastMessage("Failed to add product to cart.");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleWishlistToggle = async () => {
    const isAddingToWishlist = !wishlist;

    if (!isLoggedIn) {
      const wishlistItem = {
        id: product.id,
        name: product.name,
        unitpriceexclvat: product.unitpriceexclvat,
        vat: product.vat || 0,
        discount: product.discount || 0,
        quantity: 1,
      };

      if (isAddingToWishlist) {
        updateLocalWishlist((prev) => {
          const exists = prev.find((item) => item.id === product.id);
          if (!exists) {
            return [...prev, wishlistItem];
          }
          return prev;
        });
      } else {
        updateLocalWishlist((prev) => prev.filter((item) => item.id !== product.id));
      }
      setWishlist(isAddingToWishlist);
      setToastMessage(
        isAddingToWishlist ? "Added to wishlist!" : "Removed from wishlist!"
      );
      setToastColor("success");
      setSnackbarOpen(true);
    } else {
      try {
        if (isAddingToWishlist) {
          if (!hasExistingWishList) {
            await createDeal(product, "Wish List", 1);
          } else {
            await updateDeal(product, "Wish List", wishListRecordId, wishListItems, 1, true);
          }
        } else {
          await updateDeal(product, "Wish List", wishListRecordId, wishListItems, 0, false);
        }
        setWishlist(isAddingToWishlist);
        setToastMessage(
          isAddingToWishlist ? "Added to wishlist!" : "Removed from wishlist!"
        );
        setToastColor("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error in handleWishlistToggle:", error);
        setToastMessage("Failed to update wishlist.");
        setToastColor("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleGoToCart = () => {
    navigate("/cart");
  };

  const isProductInCart = () => {
    if (!isLoggedIn) {
      return localCartItems.some((item) => item.id === product.id);
    }
    return hasExistingOrder && dealLineItems.some(
      (item) => (item.product.id || item.product) === product.id
    );
  };

  const categoryNames = (product.productcategories || []).map((cat) => cat.name).join(", ");
  const images = [
    product.imageone,
    product.imagetwo,
    product.imagethree,
    product.imagefour,
    product.imagefive,
  ].filter((img) => img && img.trim() !== "");

  const handleThumbnailClick = (image) => {
    setSelectedImage(image);
  };

return (
    <Container sx={{ padding: "20px", fontFamily: fontFamilyall, mt: 20 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {images.length > 1 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "80px",}}>
                {images.map((img, idx) => (
                  <Card
                    key={idx}
                    sx={{
                      width: 60,
                      height: 60,
                      cursor: "pointer",
                      border: selectedImage === img ? "2px solid #8c7aa2" : "none",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    }}
                    onClick={() => handleThumbnailClick(img)}
                  >
                    <CardMedia
                      component="img"
                      image={img}
                      alt={`${product.name} thumbnail ${idx + 1}`}
                      sx={{ objectFit: "cover", width: "100%", height: "100%" }}
                    />
                  </Card>
                ))}
              </Box>
            )}
            <Card sx={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", flex: 1 }}>
              <CardMedia
                component="img"
                height="400"
                image={selectedImage || images[0] || "https://via.placeholder.com/500"}
                alt={product.name}
                sx={{
                  objectFit: "contain",
                  width: "100%",
                  height: "400px",
                  display: "block",
                  margin: "auto",
                  backgroundColor: "#f5f5f5",
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.02)" },
                }}
              />
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: "600", color: "#333", fontFamily: fontFamilyall }}>
              {product.name}
            </Typography>
            {product?.discount && product.discount > 0 ? (
              <Typography
                sx={{
                  fontFamily: fontFamilyall,
                  backgroundColor: "#ff4444",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.05)" },
                    "100%": { transform: "scale(1)" },
                  },
                }}
              >
                {product.discount}% OFF
              </Typography>
            ) : null}
          </Box>
          <Typography variant="h5" gutterBottom sx={{ color: "#8c7aa2", fontFamily: fontFamilyall }}>
            {product.description}
          </Typography>
          <Typography variant="body1" sx={{ color: "#555", mb: 2, fontFamily: fontFamilyall }}>
            Category: {categoryNames || "N/A"}
          </Typography>
          {product?.discount >= 0 ? (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: "red",
                  mb: 1,
                  textDecoration: "line-through",
                  fontFamily: fontFamilyall,
                }}
              >
                Price: {currencyCode}{product.unitpriceexclvat || "N/A"}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#8c7aa2",
                  fontWeight: "bold",
                  mb: 2,
                  fontSize: "1.25rem",
                  fontFamily: fontFamilyall,
                }}
              >
                Discounted Price: {currencyCode}{product.unitpriceincldiscount || "N/A"}
              </Typography>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ color: "#555", mb: 2, fontFamily: fontFamilyall }}
            >
              Price: {currencyCode}{product.unitpriceexclvat || "N/A"}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ mr: 2, color: "#555", fontFamily: fontFamilyall }}>Quantity:</Typography>
            <TextField
              type="number"
              value={quantity}
              onChange={handleQuantityChange}
              inputProps={{ min: 1 }}
              disabled={isProductInCart()}
              sx={{
                width: "80px",
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  fontFamily: "Poppins, sans-serif",
                  color: "#333",
                },
                "& .MuiInputBase-input:disabled": {
                  color: "#888",
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            {isProductInCart() ? (
              <Typography
                component="span"
                sx={{
                  fontFamily: fontFamilyall,
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  textTransform: "none",
                  padding: {
                    xs: "4px 10px",
                    sm: "5px 15px",
                  },
                  borderRadius: "4px",
                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.875rem",
                  },
                  minWidth: {
                    xs: "80px",
                    sm: "100px",
                  },
                  display: "inline-block",
                }}
              >
                Added to Cart
              </Typography>
            ) : (
              <Button
                variant="contained"
                onClick={handleAddToCart}
                sx={{
                  fontFamily: fontFamilyall,
                  backgroundColor: buttonColor,
                  color: "#fff",
                  textTransform: "none",
                  padding: "5px 15px",
                  borderRadius: "4px",
                  "&:hover": { backgroundColor: buttonHoverColor },
                }}
              >
                Add to Cart
              </Button>
            )}
            <Tooltip title={wishlist ? "In Wishlist" : "Add to Wishlist"}>
              <IconButton
                onClick={handleWishlistToggle}
                sx={{ color: wishlist ? "#d32f2f" : "#d32f2f" }}
              >
                {wishlist ? <Favorite /> : <FavoriteBorder />}
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Container>
  );
};

export default ProductPage;